import React, { FC } from "react";
import { useTransactions } from "../../hooks/useTransactions";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useTranslation } from "react-i18next";
import { AbsoluteCenter } from "../AbsoluteCenter";
import { Loader } from "../Loader";
import classNames from "classnames";
import styles from "./Transactions.module.scss";
import { useIntersection } from "../../hooks/useIntersection";
import isEqual from "react-fast-compare";
import { XpTransactionFragmentFragment } from "../../hooks/useXPTransactions/graphql/XPTransactionsFragment.generated";
import {
  ExperienceTransactionGameLoseDetails,
  ExperienceTransactionGameWinDetails,
  ExperienceTransactionGameDrawDetails,
  ExperienceTransactionReferralDetails,
  ExperienceTransactionRealMoneyPurchaseDetails,
  ExperienceTransactionRewardedAdDetails,
  ExperienceTransactionVipSubscriptionDetails,
  ExperienceTransactionChipPackRewardDetails,
} from "./xpDetails";
import {
  faArrowAltLeft,
  faArrowAltRight,
  faAward,
  faChartLine,
  faChessKnight,
  faClawMarks,
  faComment,
  faGift,
  faImage,
  faMagic,
  faMedal,
  faPaperPlane,
  faPiggyBank,
  faShoppingBag,
  faUndo,
  faUser,
  faUserPlus,
  faVideo,
} from "@fortawesome/pro-solid-svg-icons";
import { useRelativeTimeFormatter } from "../../hooks/useRelativeTimeFormatter";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useXPTransactions } from "../../hooks/useXPTransactions";

const isDevelopment = process.env.NODE_ENV === "development";

const AVATAR_WIDTH = 46;
const AVATAR_HEIGHT = 46;

const withTransaction = (
  Component: React.ComponentType<{
    details: any;
    earnedXP: number;
    totalXP: number;
  }>,
  Avatar: React.ComponentType<{
    transaction?: XpTransactionFragmentFragment;
  }>
) => {
  return ({
    transaction,
    previous,
    intersecting,
    earnedXP,
    totalXP,
  }: {
    transaction: XpTransactionFragmentFragment;
    previous?: XpTransactionFragmentFragment;
    intersecting: boolean;
    earnedXP?: number;
    totalXP?: number;
  }) => {
    const relativeTimeFormatter = useRelativeTimeFormatter();
    return (
      <div className="flex -mx-2">
        <div className="flex-shrink-0 px-2">
          <div
            className="overflow-hidden bg-gray-200 rounded-full flex items-center justify-center"
            style={{ width: AVATAR_WIDTH, height: AVATAR_HEIGHT }}
          >
            {intersecting && <Avatar transaction={transaction} />}
          </div>
        </div>
        <div className="px-2 whitespace-normal text-gray-200 flex-1">
          <Component
            details={transaction.details}
            earnedXP={earnedXP || 0}
            totalXP={totalXP || 0}
          />
          <div className="text-sm text-gray-400">
            {relativeTimeFormatter.format(new Date(transaction.created), {
              addSuffix: true,
            })}
          </div>
        </div>
      </div>
    );
  };
};

const FontAwesomeAvatar = ({ icon }: { icon: IconProp }) => {
  return (
    <FontAwesomeIcon
      fixedWidth={true}
      icon={icon}
      size="1x"
      className="text-black text-gray-700"
    />
  );
};

const Component: {
  [key in string]: React.ComponentType<{
    transaction: XpTransactionFragmentFragment;
    previous?: XpTransactionFragmentFragment;
    intersecting: boolean;
    earnedXP?: number;
    totalXP?: number;
  }>;
} = {
  ExperienceTransactionGameLoseDetails: withTransaction(
    ExperienceTransactionGameLoseDetails,
    () => <FontAwesomeAvatar icon={faArrowAltRight} />
  ),
  ExperienceTransactionGameWinDetails: withTransaction(
    ExperienceTransactionGameWinDetails,
    () => <FontAwesomeAvatar icon={faArrowAltRight} />
  ),
  ExperienceTransactionGameDrawDetails: withTransaction(
    ExperienceTransactionGameDrawDetails,
    () => <FontAwesomeAvatar icon={faArrowAltRight} />
  ),
  ExperienceTransactionReferralDetails: withTransaction(
    ExperienceTransactionReferralDetails,
    () => <FontAwesomeAvatar icon={faArrowAltRight} />
  ),
  ExperienceTransactionRealMoneyPurchaseDetails: withTransaction(
    ExperienceTransactionRealMoneyPurchaseDetails,
    () => <FontAwesomeAvatar icon={faArrowAltRight} />
  ),
  ExperienceTransactionRewardedAdDetails: withTransaction(
    ExperienceTransactionRewardedAdDetails,
    () => <FontAwesomeAvatar icon={faArrowAltRight} />
  ),
  ExperienceTransactionVipSubscriptionDetails: withTransaction(
    ExperienceTransactionVipSubscriptionDetails,
    () => <FontAwesomeAvatar icon={faArrowAltRight} />
  ),
  ExperienceTransactionChipPackRewardDetails: withTransaction(
    ExperienceTransactionChipPackRewardDetails,
    () => <FontAwesomeAvatar icon={faArrowAltRight} />
  ),
};

const TransactionDetails = ({
  transaction,
  previous,
  intersecting,
}: {
  transaction: XpTransactionFragmentFragment;
  previous?: XpTransactionFragmentFragment;
  intersecting: boolean;
}) => {
  const typeName = transaction?.details?.__typename!;
  const Comp = Component[typeName];
  if (Comp === undefined) {
    if (isDevelopment)
      throw new Error("Could not find component for type:" + typeName);
    else return null;
  }
  return (
    <Comp
      transaction={transaction}
      previous={previous}
      intersecting={intersecting}
      earnedXP={transaction.amount}
      totalXP={transaction.newTotal}
    />
  );
};

const Transaction = React.memo<{
  container: React.MutableRefObject<HTMLDivElement | null>;
  transaction: XpTransactionFragmentFragment;
  previous?: XpTransactionFragmentFragment;
}>(({ container, transaction, previous }) => {
  const target = React.useRef<HTMLDivElement>(null);
  const intersecting = useIntersection(target, {
    root: container,
    once: true,
    defaultIntersecting: false,
  });
  return (
    <div className="py-2 px-4" ref={target}>
      <div className="overflow-hidden text-gray-200">
        <TransactionDetails
          transaction={transaction}
          previous={previous}
          intersecting={intersecting}
        />
      </div>
    </div>
  );
}, isEqual);

export const XpTransactions = React.memo(() => {
  const { XPTransactions, fetchXPMore } = useXPTransactions();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const loadingRef = useInfiniteScroll<HTMLDivElement>(
    { root: containerRef, rootMargin: "100px" },
    () => {
      const endXPCursor = XPTransactions?.pageInfo?.endCursor ?? null;
      if (!endXPCursor) return Promise.resolve();

      return fetchXPMore(endXPCursor);
    }
  );

  const { t } = useTranslation();
  if (XPTransactions === undefined)
    return (
      <div className="flex-1 relative">
        <AbsoluteCenter>
          <Loader description={t("loading_transactions")} />
        </AbsoluteCenter>
      </div>
    );
  else if (XPTransactions.edges.length === 0)
    return (
      <div className="flex-1 relative">
        <AbsoluteCenter>
          <div className="p-4 text-center">
            <span className="text-white font-bold">
              {t("no_transactions_available")}
            </span>
          </div>
        </AbsoluteCenter>
      </div>
    );
  else
    return (
      <>
        <div className="flex-1 h-full relative">
          <div
            key="ascasasca"
            className={classNames(
              "flex",
              "flex-col",
              "-mb-2",
              "h-full",
              "overflow-auto",
              styles.scrollable
            )}
            ref={containerRef}
          >
            {XPTransactions?.edges?.map(({ node }, index) => (
              <Transaction
                transaction={node}
                previous={XPTransactions?.edges[index + 1]?.node}
                key={node.id}
                container={containerRef}
              />
            ))}
            {XPTransactions.pageInfo.hasNextPage && (
              <div ref={loadingRef} className="py-2">
                <Loader description={""} />
              </div>
            )}
          </div>
        </div>
      </>
    );
});
